/**
 * Semántica de los eventos del dashboard (ticket #365).
 *
 * **Qué** fechas mira el dashboard lo decide la definición de modelos (los campos
 * `type: 'date'` con `calendar: true`), que es exactamente lo que ofrece el engranaje
 * del calendario. Lo que la definición no sabe es la semántica del dashboard: qué
 * ícono le toca a cada fecha, qué prioridad tiene y con qué mensaje la agrupa el
 * resumen. Eso es lo que vive en este archivo.
 *
 * Antes esa información estaba repetida en tres lugares con la lista de campos
 * escrita a mano (`Dashboard.vue -> eventTypeConfig`,
 * `EventsAnalyzer.vue -> dateFieldsConfig` y
 * `OperationsSummary.vue -> dateFieldsConfig`) y no coincidían entre sí. El caso
 * concreto: en Duntower las fechas de cut off se llaman `cod` y `cop`, pero esas
 * listas las buscaban como `cut_off_documents` / `cut_off_physical`. Resultado: el
 * engranaje las ofrecía, el calendario las pintaba grises como «Cod»/«Cop» y los
 * bloques de la derecha y el resumen las ignoraban por completo.
 *
 * Al derivar los campos de la definición y la semántica de acá, el engranaje pasa a
 * gobernar los tres bloques y agregar una fecha nueva no requiere tocar tres listas.
 */

import {
  getShippingStatus,
  SHIPPING_STATUS_UPCOMING_ETA,
} from "@/utils/shippingStatus";

/** Color por defecto de los eventos que no se pintan por status de embarque. */
const DEFAULT_EVENT_COLOR = "rgb(97 173 255)";

/** Color de texto por defecto sobre esos colores. */
const DEFAULT_EVENT_TEXT_COLOR = "#000000";

/**
 * Fechas que solo traen los embarques que están en cierto status.
 *
 * Ticket #365: al tildar **ETA**, el dashboard tiene que traer únicamente las
 * operaciones en rojo («Upcoming ETA»), que son las que el usuario mira en el
 * reporte. Una operación cuya ETA ya pasó y que además salió del puerto (verde) no
 * aporta nada en el calendario y era justamente lo que se veía mezclado.
 *
 * Es una regla de Duntower: solo se aplica cuando quien llama lo pide
 * (`applyFieldStatusRules`), así que el resto de los tenants no cambia.
 */
const FIELD_STATUS_RULES = {
  "shipping.eta": [SHIPPING_STATUS_UPCOMING_ETA],
};

/**
 * ¿Este documento entra por esta fecha, según la regla de status del campo?
 *
 * Sin regla para el campo (todo lo que no sea la ETA de embarques) devuelve `true`:
 * el resto de las fechas sigue mostrándose como siempre.
 *
 * @param {string} modelName
 * @param {string} fieldKey
 * @param {Object} item
 */
export function matchesFieldStatusRule(modelName, fieldKey, item) {
  const ruleKey = `${String(modelName || "").toLowerCase()}.${String(
    fieldKey || ""
  ).toLowerCase()}`;
  const allowedStatuses = FIELD_STATUS_RULES[ruleKey];
  if (!allowedStatuses || !item) return true;
  return allowedStatuses.includes(getShippingStatus(item));
}

/**
 * Estados de embarque que el engranaje deja ocultar, además de los campos de fecha.
 *
 * El calendario pinta cada evento de embarque con el estado de su operación, y la
 * leyenda los cuenta por estado. «On board», «Upcoming ETA» y «Gate out» se
 * corresponden con una fecha que ya se destilda (ETD, ETA, Gate Out), pero
 * «To be coordinated», «Coordinated» y «Consolidated» no tienen fecha propia: sin
 * esto no había forma de sacarlos. «To be coordinated» también llega al calendario,
 * por ejemplo un embarque con ETD futura y sin fecha de carga.
 */
export const FILTERABLE_SHIPPING_STATUSES = [1, 2, 3];

/** Clave del estado en las preferencias del engranaje (mismo mapa que los campos). */
export function shippingStatusPreferenceId(status) {
  return `shipping.status.${status}`;
}

/**
 * ¿El usuario ocultó en el engranaje el estado de este embarque?
 *
 * Solo cuenta un `false` explícito: sin preferencia guardada el estado se ve, y en
 * los tenants que no ofrecen estos ítems nunca hay una.
 *
 * @param {string} modelName
 * @param {Object} item
 * @param {Object} calendarPreferences - Mapa `modelo.campo -> boolean`.
 */
export function isShippingStatusHidden(modelName, item, calendarPreferences = {}) {
  if (String(modelName || "").toLowerCase() !== "shipping" || !item) return false;
  const status = getShippingStatus(item);
  if (!FILTERABLE_SHIPPING_STATUSES.includes(status)) return false;
  return calendarPreferences[shippingStatusPreferenceId(status)] === false;
}

/**
 * Texto de ayuda (?) de cada ítem del engranaje del calendario (ticket #440).
 *
 * Describe qué trae cada fecha en Duntower, incluida su regla de status (la ETA
 * trae solo los embarques en rojo), así que solo se muestra en ese tenant. Una
 * fecha sin entrada queda sin ícono.
 */
const DASHBOARD_FIELD_HELP_KEYS = {
  "shipping.etd": "calendar.fieldHelp.shipping_etd",
  "shipping.eta": "calendar.fieldHelp.shipping_eta",
  "shipping.shipping_date": "calendar.fieldHelp.shipping_shipping_date",
  "shipping.gate_out": "calendar.fieldHelp.shipping_gate_out",
  "payment.payment_date": "calendar.fieldHelp.payment_payment_date",
  "collect.date": "calendar.fieldHelp.collect_date",
};

/**
 * Clave i18n de la ayuda de una fecha del engranaje, o '' si no tiene.
 *
 * @param {string} modelName
 * @param {string} fieldKey
 */
export function dashboardFieldHelpKey(modelName, fieldKey) {
  return (
    DASHBOARD_FIELD_HELP_KEYS[
      `${String(modelName || "").toLowerCase()}.${String(fieldKey || "").toLowerCase()}`
    ] || ""
  );
}

/** Clave i18n de la ayuda de un estado filtrable. Ver `FILTERABLE_SHIPPING_STATUSES`. */
export function shippingStatusHelpKey(status) {
  return `calendar.fieldHelp.shipping_status_${status}`;
}

/**
 * Fechas de calendario que el dashboard no muestra (ticket #424).
 *
 * El usuario pidió que no aparezcan ni en el engranaje ni en ningún bloque: no las
 * usa y le ensucian el calendario. Se ocultan solo del dashboard y no se les saca
 * `calendar: true` en la definición del modelo, porque ese flag también alimenta
 * otras vistas (por ejemplo el feed del sync de calendario).
 *
 * `payment.date` (Request Date) arrastra también el evento «Payment Status» de los
 * pagos sin pagar (`pendingEvent`), que por lo tanto tampoco aparece.
 *
 * Es una regla de Duntower: solo se aplica cuando quien llama lo pide, así que el
 * resto de los tenants no cambia. Mantener igual que `DASHBOARD_HIDDEN_FIELDS` en
 * `back/controllers/gotrading/dashboardCalendarController.js`.
 */
const DASHBOARD_HIDDEN_FIELDS = new Set([
  "shipping.cod",
  "shipping.cop",
  "shipping.expirationdate_ip",
  "payment.date",
  "event.startdate",
]);

/**
 * ¿Esta fecha está oculta del dashboard? Ver `DASHBOARD_HIDDEN_FIELDS`.
 *
 * @param {string} modelName
 * @param {string} fieldKey
 */
export function isHiddenDashboardField(modelName, fieldKey) {
  return DASHBOARD_HIDDEN_FIELDS.has(
    `${String(modelName || "").toLowerCase()}.${String(fieldKey || "").toLowerCase()}`
  );
}

/**
 * Metadatos por `<modelo en minúsculas>.<campo>`.
 *
 * - `typeKey`: etiqueta corta del tipo (se traduce al mostrarla).
 * - `shortKey`: código corto para el chip del calendario, donde la celda es angosta.
 *   Con la etiqueta completa el nombre de la operación quedaba cortado por el
 *   ellipsis («Loading date: OP…»), que es lo que se reportó como «no se lee el nro
 *   de operación». Los de Duntower (ETA, ETD, L/DATE, G/OUT, REQ. PAY, PAID, COLL)
 *   los eligió el propio usuario en el ticket #365, así que no se cambian sin
 *   avisarle. Pasan por `$t`, así que un idioma puede redefinirlos si hace falta.
 *   Solo afectan al chip del calendario: el encabezado de los listados sigue
 *   saliendo de la abreviación del modelo.
 * - `priority`: `critical` | `high` | `medium`.
 * - `summaryType`: con qué mensaje lo agrupa el resumen de operaciones. Si no hay
 *   mensaje para ese tipo, el resumen simplemente no lo muestra.
 * - `color` / `textColor`: color del chip y de la leyenda del calendario, para las
 *   fechas que no son de embarque. Los embarques se pintan con su status operativo
 *   (`STATUS_COLORS`), así que no lo declaran. Va por campo y no por modelo: la
 *   solicitud de pago y el pago efectivo son cosas distintas y con el mismo color
 *   eran indistinguibles en el calendario. Se eligieron fuera de la familia de
 *   estados (violeta y rosa) para que un pago no parezca un «Upcoming ETA» ni un
 *   cobro un «Gate out».
 * - `badgeClass`: clase del badge del panel del día.
 * - `pendingEvent`: además del evento por fecha, genera uno extra si un campo del
 *   documento tiene cierto valor (hoy: pagos con `paid` en false).
 *
 * Se incluyen también los nombres alternativos que puede tener el mismo concepto en
 * otros tenants (`cut_off_documents` junto a `cod`, por ejemplo): un tenant usa una
 * clave u otra y la que no exista nunca se resuelve.
 */
const EVENT_TYPE_METADATA = {
  // --- Embarques ---
  "shipping.eta": {
    typeKey: "ETA",
    shortKey: "ETA",
    labelKey: "Estimated Time of Arrival",
    icon: "fas fa-ship",
    priority: "critical",
    summaryType: "eta",
    badgeClass: "badge-secondary",
  },
  "shipping.etd": {
    typeKey: "ETD",
    shortKey: "ETD",
    labelKey: "Estimated Time of Departure",
    icon: "fas fa-anchor",
    priority: "high",
    summaryType: "etd",
    badgeClass: "badge-secondary",
  },
  "shipping.shipping_date": {
    typeKey: "Loading",
    shortKey: "L/DATE",
    labelKey: "Loading Date",
    icon: "fas fa-boxes",
    priority: "high",
    summaryType: "loading",
    badgeClass: "badge-secondary",
  },
  "shipping.gate_out": {
    typeKey: "Gate Out",
    shortKey: "G/OUT",
    labelKey: "Gate Out",
    icon: "fas fa-truck",
    priority: "high",
    summaryType: "gate_out",
    badgeClass: "badge-secondary",
  },
  // Cut off de documentos: `cod` en Duntower, `cut_off_documents` en otros.
  "shipping.cod": {
    typeKey: "Cut Off Docs",
    shortKey: "COD",
    labelKey: "Cut Off Documents",
    icon: "fas fa-clock",
    priority: "critical",
    summaryType: "cut_off_docs",
    badgeClass: "badge-info",
  },
  "shipping.cut_off_documents": {
    typeKey: "Cut Off Docs",
    shortKey: "COD",
    labelKey: "Cut Off Documents",
    icon: "fas fa-clock",
    priority: "critical",
    summaryType: "cut_off_docs",
    badgeClass: "badge-info",
  },
  // Cut off físico: `cop` en Duntower, `cut_off_physical` en otros.
  "shipping.cop": {
    typeKey: "Cut Off Physical",
    shortKey: "COP",
    labelKey: "Cut Off Physical",
    icon: "fas fa-clock",
    priority: "critical",
    summaryType: "cut_off_physical",
    badgeClass: "badge-info",
  },
  "shipping.cut_off_physical": {
    typeKey: "Cut Off Physical",
    shortKey: "COP",
    labelKey: "Cut Off Physical",
    icon: "fas fa-clock",
    priority: "critical",
    summaryType: "cut_off_physical",
    badgeClass: "badge-info",
  },
  "shipping.expirationdate_ip": {
    typeKey: "Import Permit",
    shortKey: "IP",
    labelKey: "Import Permit Expiration",
    icon: "fas fa-passport",
    priority: "high",
    summaryType: "import_permit",
    badgeClass: "badge-info",
  },
  "shipping.datereleased": {
    typeKey: "Date Released",
    shortKey: "REL",
    labelKey: "Date Released",
    icon: "fas fa-file-alt",
    priority: "medium",
    summaryType: "date_released",
    badgeClass: "badge-info",
  },
  "shipping.dhl_date": {
    typeKey: "DHL Date",
    shortKey: "DHL",
    labelKey: "DHL Date",
    icon: "fas fa-shipping-fast",
    priority: "medium",
    summaryType: "dhl_date",
    badgeClass: "badge-info",
  },

  // --- Pagos ---
  "payment.date": {
    typeKey: "Payment Request",
    shortKey: "REQ. PAY",
    // Violeta claro: es la solicitud, todavía no se pagó.
    color: "#c5b3e6",
    textColor: "#212529",
    labelKey: "Request Date of Payment",
    icon: "fas fa-dollar-sign",
    priority: "high",
    summaryType: "payment_request",
    badgeClass: "badge-info",
    pendingEvent: {
      typeKey: "Payment Status",
      labelKey: "Payment Status",
      icon: "fas fa-exclamation-triangle",
      priority: "critical",
      summaryType: "payment_pending",
      badgeClass: "badge-danger",
      checkField: "paid",
      checkValue: false,
    },
  },
  "payment.payment_date": {
    typeKey: "Payment",
    shortKey: "PAID",
    // Violeta pleno: la plata salió.
    color: "#6f42c1",
    textColor: "#ffffff",
    labelKey: "Payment Date",
    icon: "fas fa-credit-card",
    priority: "critical",
    summaryType: "payment",
    badgeClass: "badge-danger",
  },

  // --- Cobros ---
  "collect.date": {
    typeKey: "Collection",
    shortKey: "COLL",
    color: "#d63384",
    textColor: "#ffffff",
    labelKey: "Collection Date",
    icon: "fas fa-money-bill-wave",
    priority: "high",
    summaryType: "collection",
    badgeClass: "badge-success",
  },

  // --- Proformas ---
  "proform.proform_number_date": {
    typeKey: "Proform",
    shortKey: "PI",
    labelKey: "Proform Date",
    icon: "fas fa-file-invoice",
    priority: "medium",
    summaryType: "proform",
    badgeClass: "badge-info",
  },
  "proform.offer_validity": {
    typeKey: "Offer Validity",
    shortKey: "OFFER",
    labelKey: "Offer Validity",
    icon: "fas fa-calendar-check",
    priority: "high",
    summaryType: "offer_validity",
    badgeClass: "badge-info",
  },

  // --- Gastos ---
  "expense.date": {
    typeKey: "Expense",
    shortKey: "EXP",
    labelKey: "Expense Date",
    icon: "fas fa-receipt",
    priority: "medium",
    summaryType: "expense",
    badgeClass: "badge-info",
  },

  // --- Eventos de agenda ---
  "event.startdate": {
    typeKey: "Event",
    shortKey: "EVT",
    labelKey: "Start",
    icon: "fas fa-calendar-plus",
    priority: "medium",
    summaryType: "event_start",
    badgeClass: "badge-secondary",
    color: "rgb(73, 154, 120)",
  },
};

/** Texto legible a partir de la clave del campo, para fechas sin metadatos. */
function humanizeFieldKey(fieldKey) {
  return String(fieldKey || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Metadatos de una fecha del dashboard. Una fecha sin entrada en el mapa (un campo
 * de calendario propio de un tenant) igual funciona: se le arma un tipo a partir de
 * su clave y queda sin `summaryType`, así que aparece en el calendario y en los
 * bloques de la derecha pero no genera un mensaje del resumen.
 *
 * @param {string} modelName - Nombre del modelo tal como viene de la definición.
 * @param {string} fieldKey - Clave del campo fecha.
 * @param {string} [fieldLabel] - Etiqueta del campo según la definición del modelo.
 */
export function resolveEventTypeInfo(modelName, fieldKey, fieldLabel = "") {
  const mapKey = `${String(modelName || "").toLowerCase()}.${String(
    fieldKey || ""
  ).toLowerCase()}`;
  const metadata = EVENT_TYPE_METADATA[mapKey];

  if (metadata) {
    return {
      ...metadata,
      // El texto visible sale de la definición del modelo cuando está: es el mismo
      // que muestra el engranaje y respeta cómo lo nombró el tenant.
      label: fieldLabel || metadata.labelKey,
      color: metadata.color || DEFAULT_EVENT_COLOR,
      textColor: metadata.textColor || DEFAULT_EVENT_TEXT_COLOR,
      fieldKey,
    };
  }

  const humanized = humanizeFieldKey(fieldKey);
  return {
    typeKey: humanized,
    // Sin código definido, las primeras letras de la clave alcanzan para que el
    // chip no se coma el nombre de la operación.
    shortKey: String(fieldKey || "").slice(0, 4).toUpperCase(),
    labelKey: humanized,
    label: fieldLabel || humanized,
    icon: "fas fa-calendar",
    color: DEFAULT_EVENT_COLOR,
    textColor: DEFAULT_EVENT_TEXT_COLOR,
    priority: "medium",
    summaryType: null,
    badgeClass: "badge-secondary",
    fieldKey,
  };
}

/**
 * Arma la lista de eventos del dashboard a partir de los datos cargados.
 *
 * Recorre los campos de calendario de cada modelo (los que ofrece el engranaje) y
 * respeta las preferencias del usuario. Es la fuente del resumen de operaciones.
 *
 * @param {Object} params
 * @param {Object} params.modelData - Documentos por modelo.
 * @param {Array} params.calendarModels - `[{ modelName, fields: [{ key, label }] }]`.
 * @param {Object} params.calendarPreferences - Mapa `modelo.campo -> boolean`.
 * @param {boolean} [params.applyFieldStatusRules] - Aplica `FIELD_STATUS_RULES`
 *   (la ETA trae solo los embarques en rojo). Solo Duntower lo pide.
 * @returns {Array} Eventos ordenados por fecha.
 */
export function buildDashboardEvents({
  modelData = {},
  calendarModels = [],
  calendarPreferences = {},
  applyFieldStatusRules = false,
}) {
  const events = [];

  for (const calendarModel of calendarModels) {
    if (!calendarModel || !calendarModel.modelName) continue;
    const { modelName } = calendarModel;
    const items = modelData[modelName] || [];

    for (const field of calendarModel.fields || []) {
      if (!field || !field.key) continue;

      // Un campo destildado en el engranaje no entra en ningún bloque. El default
      // es visible para que una fecha nueva no quede oculta hasta que el usuario
      // abra la configuración.
      const fieldId = `${modelName}.${field.key}`;
      if (calendarPreferences[fieldId] === false) continue;

      const info = resolveEventTypeInfo(modelName, field.key, field.label);

      for (const item of items) {
        if (!item) continue;
        if (
          applyFieldStatusRules &&
          !matchesFieldStatusRule(modelName, field.key, item)
        ) {
          continue;
        }
        if (isShippingStatusHidden(modelName, item, calendarPreferences)) continue;
        const rawDate = item[field.key];
        if (!rawDate) continue;
        const date = new Date(rawDate);
        if (Number.isNaN(date.getTime())) continue;

        events.push({
          id: `${item._id}_${field.key}`,
          modelName,
          itemId: item._id,
          fieldKey: field.key,
          fieldLabel: info.label,
          date,
          rawDate,
          priority: info.priority,
          summaryType: info.summaryType,
          typeKey: info.typeKey,
          icon: info.icon,
          color: info.color,
          item,
          isPending: false,
        });

        // Evento extra derivado del estado del documento (pago no pagado).
        const pending = info.pendingEvent;
        if (pending && item[pending.checkField] === pending.checkValue) {
          events.push({
            id: `${item._id}_${field.key}_pending`,
            modelName,
            itemId: item._id,
            fieldKey: field.key,
            fieldLabel: pending.labelKey,
            date,
            rawDate,
            priority: pending.priority,
            summaryType: pending.summaryType,
            typeKey: pending.typeKey,
            icon: pending.icon,
            color: info.color,
            item,
            isPending: true,
          });
        }
      }
    }
  }

  return events.sort((a, b) => a.date - b.date);
}
